import { z } from 'zod'
import { createRequiredLatinCharacterSchema } from '@/shared/lib/latinCharacterValidation'

export const changePasswordSchema = z
  .object({
    oldPassword: z.string().min(8).max(70),
    newPassword: z.string().min(8).max(70),
    newPasswordRepeat: z.string().min(8).max(70),
  })
  .refine((data) => data.newPassword === data.newPasswordRepeat, {
    message: 'New passwords must match',
    path: ['newPasswordRepeat'],
  })

export const requestResetPasswordSchema = z.object({
  email: z.email().max(256),
})

export const resetPasswordPayloadSchema = z
  .object({
    token: z.string(),
    password: z
      .string()
      .min(8, 'min_length')
      .max(70, 'max_length')
      .regex(/[A-Z]/, 'one_uppercase_letter')
      .regex(/[\W_]/, 'one_special_char'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'passwords_do_not_match',
    path: ['confirmPassword'],
  })

export type UserChangePasswordPayload = z.infer<typeof changePasswordSchema>

export type OperatorOrganization = {
  id: string
  name: string
  localName: string
  activatedAt: string | null
  isMandatoryInfoComplete: boolean
  certificateFileName: string | null
  businessRegistrationNumber: string | null
  placeOfRegistration: string | null
  maxNumberInstallations: number
  street: string
  street2: string
  city: string
  postCode: string
  country: string
  streetNumber: string
  subDivision: string
  poBox: string
  contactName: string
  contactEmail: string
  contactPhoneNumber: string
  localStreet: string
  localStreet2: string
  localStreetNumber: string
  localSubDivision: string
  localPostCode: string
  localPoBox: string
  localCity: string
}

export type OperatorUserSession = {
  id: string
  email: string
  firstName: string
  lastName: string
  isEmailVerified: boolean
  organization: OperatorOrganization
  language: string
  permissions: string[]
  passwordResetRequired: boolean
}

const organizationDetailsFields = {
  organizationName: createRequiredLatinCharacterSchema(
    'Organization name must use Latin characters',
    { min: 2 }
  ),
  localName: z.string().min(2),
  placeOfRegistration: z.string().length(2),
  businessRegistrationNumber: z.string().min(1).optional(),
  certificateFileName: z.string().optional(),
}

export const organizationDetailsSchema = z.object(organizationDetailsFields)
export type OrganizationDetails = z.infer<typeof organizationDetailsSchema>
